import React from 'react';

type UploadStatus = 'uploading' | 'processing' | 'completed' | 'error';

interface UploadProgressBarProps {
  progress?: number;
  status?: UploadStatus;
  showPercentage?: boolean;
  className?: string;
}

/**
 * Reusable progress bar component for file uploads
 * @param progress - Progress percentage (0-100)
 * @param status - Current status ('uploading', 'processing', 'completed', 'error')
 * @param showPercentage - Whether to show percentage text
 * @param className - Additional CSS classes
 */
const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
  progress = 0,
  status = 'uploading',
  showPercentage = true,
  className = ''
}) => {
  // Determine bar color based on status
  const getBarColor = (): string => {
    switch (status) {
      case 'error':
        return 'bg-red-500';
      case 'completed':
        return 'bg-green-500';
      case 'processing':
        return 'bg-blue-400';
      case 'uploading':
      default:
        return 'bg-blue-500';
    }
  };

  // Ensure progress is within valid range
  const safeProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Progress bar container */}
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${getBarColor()}`}
          style={{ width: `${safeProgress}%` }}
        />
      </div>

      {/* Percentage display */}
      {showPercentage && (
        <div className="min-w-[45px] text-right text-sm text-gray-600">
          {status === 'processing' ? (
            <span className="animate-pulse">...</span>
          ) : (
            <span>{`${safeProgress}%`}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadProgressBar;
